<template>
  <v-container
    v-if="dataSource"
    class="bordered bg-white"
    style="min-width: 1200px; min-height: 600px">
    <v-row>
      <v-col class="text-right">
        <v-btn
          class="mr-2 text-capitalize"
          color="primary"
          flat
          prepend-icon="mdi-tray-arrow-down"
          rounded="xl"
          variant="outlined"
          @click="downloadPng">
          {{ $t(`actions.download`) }}
        </v-btn>
      </v-col>
    </v-row>

    <Bar
      :key="nodeId"
      ref="chartRef"
      :data="chartData"
      :options="chartOptions"
      style="background-color: transparent" />
  </v-container>
</template>

<script setup>
import { onMounted, ref, computed } from "vue";
import { useTranslation } from "i18next-vue";
import { Bar } from "vue-chartjs";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  Colors
} from "chart.js";
import {
  chartFields,
  prepareBreakdownChartData,
  costBreakdownChartOptions,
  customCanvasBackgroundColor,
  downloadChartPng
} from "@/utils/costBreakdown/costBreakdownChart";

import fakeCostBreakdown from "@/utils/costBreakdown/fakeCostBreakdown";
import { useSpaceStore } from "../../../stores/spaceStore";
import { watch } from "vue";
import { useCalculationStore } from "@/stores/calculationStore";

const calculationStore = useCalculationStore();

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  ChartDataLabels,
  customCanvasBackgroundColor,
  Colors
);

const spaceStore = useSpaceStore();
const { t } = useTranslation();

const props = defineProps({
  nodeId: {
    type: String,
    default: null
  },
  parentId: {
    type: String,
    default: null
  },
  kind: {
    type: String,
    default: "part"
  },
  fakeData: {
    type: Boolean,
    default: false
  }
});

const dataSource = ref(null);
const chartRef = ref(null);
const title = ref(null);

const titleField = computed(() => {
  switch (props.kind) {
    case "part":
      return props.parentId ? "name" : "variant_name";
    case "manufacturingStep":
      return "manufacturing_step_name";
    default:
      return "name";
  }
});

onMounted(() => {
  init();
});

watch(props.nodeId, () => {
  init();
});

watch(calculationStore.calculating, () => {
  if (!calculationStore.calculating) {
    init();
  }
});

function init() {
  let prefix = "";
  if (props.nodeId) {
    if (props.fakeData) {
      dataSource.value = fakeCostBreakdown(props.kind, null);
      prefix = spaceStore.calculationElements[props.nodeId][titleField.value];
    } else {
      dataSource.value = spaceStore.calculationElements[props.nodeId];
      prefix = dataSource.value[titleField.value];
    }
  } else if (props.parentId) {
    if (props.fakeData) {
      dataSource.value = [
        fakeCostBreakdown(
          props.kind,
          "very long label 1 fakeCostBreakdown asdasd sdasd"
        ),
        fakeCostBreakdown(
          props.kind,
          "very long label 2 fakeCostBreakdown asdasd"
        ),
        fakeCostBreakdown(props.kind, "very long label 3 sdasd"),
        fakeCostBreakdown(
          props.kind,
          "very long label 4 fakeCostBreakdown asdasd sdasd dfsdfds"
        )
      ];
      prefix = spaceStore.calculationElements[props.parentId][titleField.value];
    } else {
      // There is only one variant by now so we can return it directly
      // dataSource.value = spaceStore.calculationElements[props.parentId];
      prefix = spaceStore.calculationElements[props.parentId].name;
      // Multiple variants case
      //
      const variant = spaceStore.calculationElements[props.parentId].variant;
      dataSource.value = Object.values(spaceStore.calculationElements).filter(
        (element) => element.variant === variant
      );
    }
  }
  title.value = prefix + " - " + t("entities.variant.tabs.costBreakdown");
}

const downloadPng = () => downloadChartPng(chartRef.value.$el);

const isMultiple = computed(() => Array.isArray(dataSource.value));

const datasets = computed(() => {
  if (!dataSource.value) return [];

  if (isMultiple.value) {
    return dataSource.value.map((part) => {
      return {
        data: prepareBreakdownChartData(part, props.kind),
        label: part.variant_name
      };
    });
  } else {
    return [
      {
        data: prepareBreakdownChartData(dataSource.value, props.kind),
        backgroundColor: "#CADDF9"
      }
    ];
  }
});

const chartData = computed(() => {
  return {
    labels: chartFields(props.kind).map((field) =>
      t(`entities.${props.kind}.fields.${field}`)
    ),
    datasets: datasets.value
  };
});

const chartOptions = computed(() =>
  costBreakdownChartOptions(title.value, isMultiple.value)
);
</script>
